import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Flex, Form, Input, InputNumber, Modal, Table, Typography } from "antd";
import { useState } from "react";
import { useNavigate } from "react-router";
import { api, unwrap } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";
import { ExportButton } from "@/components/list/ExportButton";
import { DateRangeFilter, SearchFilter, StatusFilter, useSearchFilter, useStatusFilter } from "@/components/list/filters";
import { useDateRange, useListPage } from "@/components/list/useListPage";
import { SkuSelect } from "../catalog/mapping";
import { NoWms, ReceiptStatusTag, useWmsWarehouse, type Receipt } from "./common";

const STATUS_CHOICES = ["active", "done", "cancelled", "all"] as const;
const RECEIPT_STATUSES = ["open", "receiving", "done", "cancelled"] as const;
// A choice on screen is one status or a group of them.
const STATUS_PARAM: Record<(typeof STATUS_CHOICES)[number], string | undefined> = { active: "open,receiving", done: "done", cancelled: "cancelled", all: undefined };
type NewReceipt = { supplier?: string; reference?: string; note?: string; lines: { skuId?: string; expected?: number; unitCost?: number | null }[] };

/** Gudang > Penerimaan: deliveries expected and received (App Flow §8.1). */
export function Inbound() {
  const { t, formatDateTime, formatNumber } = useI18n();
  const { me, can } = useAuth();
  const navigate = useNavigate();
  const { current, loading } = useWmsWarehouse();
  const status = useStatusFilter(STATUS_CHOICES, "active");
  const search = useSearchFilter();
  const range = useDateRange();
  const page = useListPage();
  const [creating, setCreating] = useState(false);
  const tz = me?.workspace?.timezone;

  const statusParam = STATUS_PARAM[status.value];
  const filters = { warehouseId: current?.id ?? "", ...(statusParam ? { status: statusParam } : {}), ...search.query, ...range.query };
  const list = useQuery({
    queryKey: ["inbound", filters, page.query],
    queryFn: () => unwrap(api.GET("/v1/inbound", { params: { query: { ...filters, ...page.query } } })),
    enabled: !!current,
    placeholderData: keepPreviousData,
  });

  if (!loading && !current) return <NoWms />;
  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
          {t("wms.in.title")}
        </Typography.Title>
        {can("warehouse.manage") && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
            {t("wms.in.add")}
          </Button>
        )}
      </Flex>
      <Flex gap={12} wrap style={{ marginBottom: 16 }}>
        <SearchFilter filter={search} placeholder={t("wms.in.search")} />
        <StatusFilter
          filter={status}
          options={[
            { value: "active", label: t("list.openOnly") },
            { value: "done", label: t("wms.in.status.done") },
            { value: "cancelled", label: t("wms.in.status.cancelled") },
            { value: "all", label: t("list.allStatuses") },
          ]}
        />
        <DateRangeFilter range={range} />
        <div style={{ flex: 1 }} />
        <ExportButton
          path="/v1/inbound/export"
          query={filters}
          filename={t("wms.in.title")}
          columns={[
            { key: "number", label: t("wms.in.number") },
            { key: "warehouseName", label: t("orders.warehouse") },
            { key: "supplier", label: t("wms.in.supplier") },
            { key: "reference", label: t("wms.in.reference") },
            { key: "status", label: t("common.status"), values: Object.fromEntries(RECEIPT_STATUSES.map((v) => [v, t(`wms.in.status.${v}`)])) },
            { key: "expected", label: t("wms.in.expected"), kind: "number" },
            { key: "received", label: t("wms.in.received"), kind: "number" },
            { key: "note", label: t("inventory.note") },
            { key: "createdBy", label: t("list.createdBy") },
            { key: "createdAt", label: t("list.createdAt"), kind: "datetime" },
            { key: "completedAt", label: t("list.completedAt"), kind: "datetime" },
          ]}
        />
      </Flex>
      <Table<Receipt>
        rowKey="id"
        loading={list.isFetching}
        dataSource={list.data?.items}
        pagination={page.pagination(list.data)}
        locale={{ emptyText: t("wms.in.empty") }}
        onRow={(r) => ({ onClick: () => navigate(`/app/warehouse/inbound/${r.id}`), style: { cursor: "pointer" } })}
        columns={[
          { title: t("wms.in.number"), dataIndex: "number", width: 150, render: (v: string) => <Typography.Text strong className="z-num">{v}</Typography.Text> },
          { title: t("wms.in.supplier"), dataIndex: "supplier", render: (v: string, r) => [v, r.reference].filter(Boolean).join(" · ") || "—" },
          { title: t("common.status"), dataIndex: "status", width: 160, render: (s: Receipt["status"]) => <ReceiptStatusTag status={s} /> },
          {
            title: t("wms.in.received"),
            width: 140,
            align: "right",
            render: (_, r) => (
              <span className="z-num">
                {formatNumber(r.received)} / {formatNumber(r.expected)}
              </span>
            ),
          },
          { title: t("orders.orderedAt"), dataIndex: "createdAt", width: 170, render: (v: string) => formatDateTime(v, tz) },
        ]}
      />
      {current && <CreateReceipt open={creating} warehouseId={current.id} onClose={() => setCreating(false)} />}
    </>
  );
}

function CreateReceipt({ open, warehouseId, onClose }: { open: boolean; warehouseId: string; onClose: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [form] = Form.useForm<NewReceipt>();
  const save = useMutation({
    mutationFn: (v: NewReceipt) =>
      unwrap(
        api.POST("/v1/inbound", {
          body: {
            warehouseId,
            supplier: v.supplier?.trim() ?? "",
            reference: v.reference?.trim() ?? "",
            note: v.note?.trim() ?? "",
            lines: v.lines.filter((l) => l.skuId && l.expected).map((l) => ({ skuId: l.skuId!, expected: l.expected!, unitCost: l.unitCost ?? null })),
          },
        }),
      ),
    onSuccess: (r) => {
      void queryClient.invalidateQueries({ queryKey: ["inbound"] });
      onClose();
      navigate(`/app/warehouse/inbound/${r.id}`);
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  return (
    <Modal open={open} width={720} title={t("wms.in.add")} okText={t("common.save")} cancelText={t("common.cancel")} confirmLoading={save.isPending} onOk={() => form.submit()} onCancel={onClose} destroyOnHidden>
      <Form form={form} layout="vertical" requiredMark={false} initialValues={{ lines: [{}] }} onFinish={(v) => save.mutate(v)}>
        <Flex gap={12}>
          <Form.Item name="supplier" label={t("wms.in.supplier")} style={{ flex: 1 }}>
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item name="reference" label={t("wms.in.reference")} style={{ flex: 1 }}>
            <Input maxLength={200} />
          </Form.Item>
        </Flex>
        <Typography.Text strong>{t("wms.in.lines")}</Typography.Text>
        <Form.List name="lines">
          {(fields, { add, remove }) => (
            <div style={{ marginTop: 8 }}>
              {fields.map((f) => (
                <Flex key={f.key} gap={8} align="start">
                  <Form.Item name={[f.name, "skuId"]} style={{ flex: 1 }} rules={[{ required: true, message: t("field.required") }]}>
                    <SkuSelect />
                  </Form.Item>
                  <Form.Item name={[f.name, "expected"]} rules={[{ required: true, message: t("field.required") }]}>
                    <InputNumber min={1} max={1_000_000} precision={0} placeholder={t("wms.in.expected")} style={{ width: 130 }} />
                  </Form.Item>
                  <Form.Item name={[f.name, "unitCost"]} tooltip={t("wms.in.unitCostHelp")}>
                    <InputNumber<number> min={0} max={1_000_000_000_000} precision={0} prefix="Rp" placeholder={t("wms.in.unitCost")} style={{ width: 170 }} />
                  </Form.Item>
                  {fields.length > 1 && <Button type="text" icon={<MinusCircleOutlined />} onClick={() => remove(f.name)} />}
                </Flex>
              ))}
              <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({})} block>
                {t("wms.in.addLine")}
              </Button>
            </div>
          )}
        </Form.List>
        <Form.Item name="note" label={t("wms.in.note")} style={{ marginTop: 16 }}>
          <Input.TextArea rows={2} maxLength={200} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
